/**
 * Past competitive gap reports — lets the operator recall any prior
 * generation from `/competitive` without re-billing Claude.
 *
 * Reads from marketing.competitive_gap_report (populated by the
 * competitor-gaps Edge Function on every successful run).
 */
import { useQuery } from '@tanstack/react-query';
import { marketing } from '@/lib/supabase/marketing';

export interface GapReportRow {
  id: string;
  generated_at: string;
  window_days: number;
  headline: string | null;
  gap_count: number | null;
  strength_count: number | null;
}

export interface GapReportFull extends GapReportRow {
  payload_json: unknown;
  response_json: {
    headline?: string;
    gaps?: unknown[];
    strengths?: unknown[];
    [k: string]: unknown;
  };
}

export function useCompetitiveGapReports(limit = 20) {
  return useQuery({
    queryKey: ['competitive-gap-reports', limit],
    queryFn: async (): Promise<GapReportRow[]> => {
      const { data, error } = await marketing
        .from('competitive_gap_report')
        .select('id, generated_at, window_days, headline, gap_count, strength_count')
        .eq('archived', false)
        .order('generated_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as unknown as GapReportRow[];
    },
    staleTime: 60_000,
  });
}

export function useCompetitiveGapReport(id: string | null) {
  return useQuery({
    queryKey: ['competitive-gap-report', id],
    enabled: !!id,
    queryFn: async (): Promise<GapReportFull | null> => {
      if (!id) return null;
      const { data, error } = await marketing
        .from('competitive_gap_report')
        .select('id, generated_at, window_days, headline, gap_count, strength_count, payload_json, response_json')
        .eq('id', id)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as unknown as GapReportFull | null;
    },
    staleTime: 5 * 60_000,
  });
}
